import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { GearIcon, PlusIcon, UsersIcon } from '@phosphor-icons/react';
import {
  createUser,
  fetchAccount,
  fetchAudit,
  fetchUsers,
  patchAccount,
  type AuditEntry,
  type StaffUserItem,
} from '@/features/account/api';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, PageHeader, StatusChip, fmtMoney, fmtRelativeTime } from '@smena/ui';

export function AccountPage() {
  const accountQ = useQuery({ queryKey: ['account'], queryFn: fetchAccount });
  const usersQ = useQuery({ queryKey: ['account-users'], queryFn: fetchUsers });
  const auditQ = useQuery({ queryKey: ['account-audit'], queryFn: fetchAudit, refetchInterval: 15_000 });

  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [profileDirty, setProfileDirty] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useState(() => {
    // Пустой self-init.
  });

  // Инициализация формы профиля из данных
  if (accountQ.data && !profileDirty && name === '') {
    if (accountQ.data.name !== name) setName(accountQ.data.name);
  }

  const saveProfile = useMutation({
    mutationFn: () => patchAccount({ name }),
    onSuccess: () => {
      setProfileDirty(false);
      void qc.invalidateQueries({ queryKey: ['account'] });
      toast.success('Профиль сохранён');
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <PageHeader
          icon={<GearIcon weight="bold" />}
          title="Аккаунт"
          description="Ваша компания, сотрудники, тариф и журнал того, кто что менял."
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Профиль */}
        <Card>
          <CardHeader>
            <CardTitle>Профиль</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label>Название компании</Label>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setProfileDirty(true);
                }}
              />
            </div>
            <div className="text-xs text-muted">
              slug: <code>{accountQ.data?.slug}</code> · тип: <StatusChip tone={accountQ.data?.kind === 'demo' ? 'amber' : 'accent'}>{accountQ.data?.kind}</StatusChip>{' '}
              · создан {accountQ.data ? fmtRelativeTime(accountQ.data.createdAt) : ''}
            </div>
            <Button
              onClick={() => saveProfile.mutate()}
              disabled={!profileDirty || saveProfile.isPending}
            >
              Сохранить профиль
            </Button>
          </CardContent>
        </Card>

        {/* Подписка (пока read-only заглушка из plan jsonb) */}
        <Card>
          <CardHeader>
            <CardTitle>Подписка</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <PlanRow label="Базовая часть" value={accountQ.data?.plan.base_rub as number | undefined} />
            <PlanRow label="За объект" value={accountQ.data?.plan.per_property_rub as number | undefined} />
            <div className="flex items-center justify-between">
              <span className="text-muted">Объектов сейчас:</span>
              <span>{accountQ.data?.propertiesCount ?? 0}</span>
            </div>
            {(accountQ.data?.plan.alpha as boolean | undefined) ? (
              <StatusChip tone="green">Условия зафиксированы</StatusChip>
            ) : null}
            <div className="text-xs text-muted">
              Счёт выставляем вручную, онлайн-оплата появится позже.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Пользователи */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <UsersIcon weight="bold" className="size-4 text-accent-fg" /> Пользователи ({usersQ.data?.length ?? 0})
          </CardTitle>
          <InviteUserButton />
        </CardHeader>
        <CardContent>
          {usersQ.data && usersQ.data.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="pb-2">Email</th>
                  <th className="pb-2">Имя</th>
                  <th className="pb-2">Роль</th>
                  <th className="pb-2">Последний вход</th>
                </tr>
              </thead>
              <tbody>
                {usersQ.data.map((u: StaffUserItem) => (
                  <tr key={u.id} className="border-t border-line/40">
                    <td className="py-2 font-mono text-xs">{u.email}</td>
                    <td className="py-2">{u.name}</td>
                    <td className="py-2">
                      <StatusChip tone={u.role === 'owner' ? 'accent' : 'muted'}>{u.role}</StatusChip>
                    </td>
                    <td className="py-2 text-xs text-muted">{u.lastLoginAt ? fmtRelativeTime(u.lastLoginAt) : 'никогда'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center text-sm text-muted">Пользователей нет.</div>
          )}
        </CardContent>
      </Card>

      {/* Аудит */}
      <Card>
        <CardHeader>
          <CardTitle>История изменений</CardTitle>
        </CardHeader>
        <CardContent>
          {auditQ.data && auditQ.data.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="pb-2">Когда</th>
                  <th className="pb-2">Кто</th>
                  <th className="pb-2">Действие</th>
                  <th className="pb-2">Детали</th>
                </tr>
              </thead>
              <tbody>
                {auditQ.data.map((e: AuditEntry, i: number) => (
                  <tr key={i} className="border-t border-line/40">
                    <td className="py-2 text-xs text-muted">{fmtRelativeTime(e.ts)}</td>
                    <td className="py-2 font-mono text-xs">{e.actor.slice(0, 8)}</td>
                    <td className="py-2">{e.action}</td>
                    <td className="py-2 truncate text-xs text-muted">
                      {Object.entries(e.payload)
                        .filter(([k]) => k !== 'userId')
                        .map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`)
                        .join(', ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center text-sm text-muted">Пока ничего не менялось.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PlanRow({ label, value }: { label: string; value?: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted">{label}:</span>
      <span className="tabular-nums">{value ? fmtMoney(value) : '-'}</span>
    </div>
  );
}

function InviteUserButton() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'owner' | 'operator'>('operator');
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => createUser({ email, name, password, role }),
    onSuccess: () => {
      setOpen(false);
      setEmail('');
      setName('');
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['account-users'] });
      toast.success('Пользователь создан');
    },
    onError: (err: Error) => toast.error(`Ошибка: ${err.message}`),
  });
  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)}>
        <PlusIcon weight="bold" /> Пригласить
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex flex-col gap-1">
        <Label>Email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company" />
      </div>
      <div className="flex flex-col gap-1">
        <Label>Имя</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <Label>Временный пароль</Label>
        <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="≥ 8 символов" />
      </div>
      <div className="flex flex-col gap-1">
        <Label>Роль</Label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'owner' | 'operator')}
          className="h-10 rounded border border-line-control bg-input px-2 text-sm"
        >
          <option value="operator">operator</option>
          <option value="owner">owner</option>
        </select>
      </div>
      <Button
        size="sm"
        onClick={() => mut.mutate()}
        disabled={!email || !password || !name || mut.isPending}
      >
        Создать
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Отмена
      </Button>
    </div>
  );
}
